import { formatPhoneNumber, parsePhoneNumber } from '@juvare/common-phone-number';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import { FormControl, InputLabel, OutlinedInput } from '@mui/material';
import React from 'react';
import { useCommonFormUiTranslation } from '../hooks';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription, FormTooltip } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, validateUniqueInList, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { type } from './types';
const WidgetFormPhone = (props) => {
    var _a, _b, _c, _d, _e, _f;
    const phoneConfig = getPhoneConfig(props.config);
    const value = (_a = props.internalData.value) !== null && _a !== void 0 ? _a : formatPhoneNumber(props.getDataString(), phoneConfig);
    const error = getVisibleError(props);
    const formLabelSuffix = getFormLabelSuffix(props);
    const elementLabel = formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label;
    const readOnly = props.config.readOnly || ((_b = props.rootParams.formParams) === null || _b === void 0 ? void 0 : _b.readOnly);
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, required: (_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.required, error: !!error, disabled: props.config.disabled },
        React.createElement(InputLabel, { htmlFor: props.id, variant: 'outlined', required: (_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.required, error: !!error, disabled: props.config.disabled }, elementLabel),
        React.createElement(FormTooltip, { tooltip: props.config.tooltip },
            React.createElement(OutlinedInput, { id: props.id, label: elementLabel, fullWidth: true, size: 'medium', type: 'tel', required: (_e = props.config.validate) === null || _e === void 0 ? void 0 : _e.required, error: !!error, value: value, disabled: props.config.disabled, readOnly: readOnly, onChange: (event) => {
                    if (readOnly) {
                        return;
                    }
                    const eValue = event.target.value;
                    const parsedValue = parsePhoneNumber(eValue, phoneConfig);
                    if (parsedValue) {
                        props.setDataString(parsedValue);
                        props.internalData.value = eValue;
                    }
                    else {
                        props.setDataString(eValue);
                        props.internalData.value = eValue;
                    }
                    renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                }, onBlur: (event) => {
                    if (readOnly) {
                        return;
                    }
                    const eValue = event.target.value;
                    const parsedValue = parsePhoneNumber(eValue, phoneConfig);
                    if (parsedValue) {
                        const formattedValue = formatPhoneNumber(parsedValue, phoneConfig);
                        if (formattedValue !== eValue) {
                            props.internalData.value = formattedValue;
                            props.update();
                            renderForm(props);
                        }
                    }
                }, inputProps: Object.assign(Object.assign({}, (props.editMode ? { tabIndex: -1 } : {})), { 'aria-describedby': ariaDescribedBy }), autoFocus: (_f = (!props.editMode && props.config.autoFocus)) !== null && _f !== void 0 ? _f : false })),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
function getPhoneConfig(config) {
    return config.regionCode ? { regionCode: config.regionCode } : undefined;
}
const WidgetFormPhoneEditor = (props) => {
    const { t } = useCommonFormUiTranslation();
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: t('widget.widgetInput.phone') + ': ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            label: true,
            appendRowIndexToLabel: true,
            readOnly: true,
            description: true,
            tooltip: true,
            autoFocus: true,
        }), buildValidateTab(props, { required: true, uniqueInList: true }), buildHiddenTab(props, { t, allowDisable: true }), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormPhone, Object.assign({}, props))));
};
export const widgetFormPhone = {
    type,
    validate: (props) => {
        var _a, _b, _c, _d, _e;
        if (isSkipValidate(props)) {
            return;
        }
        const phoneConfig = getPhoneConfig(props.config);
        const value = props.getDataStringOrUndefined();
        if (((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) && !value) {
            return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
        }
        const parsedValue = value && parsePhoneNumber(value, phoneConfig);
        if (value && !parsedValue) {
            return props.config.label + ' is invalid';
        }
        if (((_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.uniqueInList) || ((_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.uniqueInGrid)) {
            const error = validateUniqueInList(props, value, props.dataPath);
            if (error) {
                return (_e = props.config.validate.uniqueInListMessage) !== null && _e !== void 0 ? _e : error;
            }
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        const phoneConfig = getPhoneConfig(props.config);
        const value = props.getDataStringOrUndefined();
        if (value) {
            return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), formatPhoneNumber(value, phoneConfig));
        }
        else {
            return [];
        }
    },
    component: WidgetFormPhone,
    icon: React.createElement(LocalPhoneIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormPhoneEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'phone', label: 'Phone' }),
};
